import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { ToggleRight, ShieldCheck, Zap, Clock, CheckCircle, AlertTriangle, Eye, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';

export function SafetyValvePanel() {
  const [mode, setMode] = useState<'manual' | 'auto'>('manual');
  const auditDaysElapsed = 18;
  const auditDaysTotal = 30;
  const auditProgress = Math.round((auditDaysElapsed / auditDaysTotal) * 100);

  const handleToggle = (checked: boolean) => {
    if (checked && auditDaysElapsed < auditDaysTotal) {
      toast({
        title: 'Audit Period Active',
        description: `${auditDaysTotal - auditDaysElapsed} days remain in the 30-day audit window. You can still enable Full Auto, but we recommend completing the review period.`,
        variant: 'destructive',
      });
    }
    setMode(checked ? 'auto' : 'manual');
    toast({
      title: checked ? 'Automated PRI Export Enabled' : 'Manual Review Mode Enabled',
      description: checked
        ? 'All PRI-validated exports will be pushed automatically to the EHR.'
        : 'Every export will require manual admin approval before reaching the EHR.',
    });
  };

  const stats = {
    totalCasesReviewed: 47,
    autoApprovalMatch: 44,
    manualOverrides: 3,
    accuracyRate: 93.6,
  };

  return (
    <div className="space-y-4">
      {/* Main toggle card */}
      <Card className={cn(
        'border-2 transition-colors',
        mode === 'auto' ? 'border-primary/30 bg-primary/5' : 'border-amber-500/30 bg-amber-50/30'
      )}>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <ToggleRight className="w-5 h-5 text-primary" />
            Export Safety Valve
          </CardTitle>
          <CardDescription>
            Control whether LINK exports flow automatically to your EHR or require manual admin review
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 rounded-lg border bg-card">
            <div className="flex items-center gap-4">
              {mode === 'manual' ? (
                <div className="p-2.5 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                  <Eye className="w-6 h-6 text-amber-600" />
                </div>
              ) : (
                <div className="p-2.5 rounded-lg bg-primary/10">
                  <Zap className="w-6 h-6 text-primary" />
                </div>
              )}
              <div>
                <p className="font-semibold">
                  {mode === 'manual' ? 'Manual Review Mode' : 'Automated PRI Export'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {mode === 'manual'
                    ? 'Every export queued for admin approval before EHR push'
                    : 'PRI-validated exports pushed to EHR automatically'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground">Manual</span>
              <Switch checked={mode === 'auto'} onCheckedChange={handleToggle} />
              <span className="text-xs text-muted-foreground">Auto</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 30-day audit progress */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary" />
            30-Day Audit Window
          </CardTitle>
          <CardDescription>
            Review LINK's accuracy before enabling Full Auto export
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Progress value={auditProgress} className="flex-1 h-3" />
            <span className="text-sm font-medium whitespace-nowrap">
              Day {auditDaysElapsed} / {auditDaysTotal}
            </span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Cases Reviewed" value={stats.totalCasesReviewed} icon={FileText} />
            <StatCard label="Auto-Match" value={stats.autoApprovalMatch} icon={CheckCircle} accent="primary" />
            <StatCard label="Manual Overrides" value={stats.manualOverrides} icon={AlertTriangle} accent="amber" />
            <StatCard label="Accuracy Rate" value={`${stats.accuracyRate}%`} icon={ShieldCheck} accent="blue" />
          </div>

          <Separator />

          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground">Audit Period Activity</p>
            <div className="space-y-1.5">
              {[
                { day: 18, event: 'Case #COG-0428 — Admin confirmed CPT match. No overrides.', type: 'match' as const },
                { day: 17, event: 'Case #COG-0425 — Admin added modifier -59 not suggested by LINK. Override logged.', type: 'override' as const },
                { day: 16, event: 'Case #COG-0422 — Admin confirmed CPT match. Noted LINK caught missing laterality.', type: 'match' as const },
                { day: 15, event: 'Case #COG-0419 — Admin confirmed. LINK pre-emptively flagged timely filing risk.', type: 'match' as const },
                { day: 14, event: 'Case #COG-0416 — Admin override: removed CPT 20680 (not performed). Override logged.', type: 'override' as const },
              ].map((entry, i) => (
                <div key={i} className="flex items-start gap-2 text-xs p-2 rounded border bg-muted/20">
                  {entry.type === 'match' ? (
                    <CheckCircle className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
                  ) : (
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" />
                  )}
                  <div>
                    <span className="text-muted-foreground">Day {entry.day}:</span>{' '}
                    <span>{entry.event}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {auditDaysElapsed >= auditDaysTotal && mode === 'manual' && (
            <div className="p-3 rounded-lg border border-primary/30 bg-primary/5">
              <p className="text-sm font-medium text-primary">
                ✓ Audit period complete — your team reviewed {stats.totalCasesReviewed} cases with {stats.accuracyRate}% accuracy. Ready for Full Auto.
              </p>
              <Button size="sm" className="mt-2 gap-1" onClick={() => handleToggle(true)}>
                <Zap className="w-3 h-3" />
                Enable Full Auto
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, accent }: { label: string; value: string | number; icon: React.ElementType; accent?: string }) {
  const accentMap: Record<string, string> = {
    emerald: 'text-primary',
    amber: 'text-amber-600',
    blue: 'text-blue-600',
  };
  return (
    <div className="p-3 rounded-lg border bg-card text-center">
      <Icon className={cn('w-4 h-4 mx-auto mb-1', accent ? accentMap[accent] : 'text-muted-foreground')} />
      <p className="text-lg font-bold">{value}</p>
      <p className="text-[10px] text-muted-foreground">{label}</p>
    </div>
  );
}
